import type { Brand } from '@deizi/shared';
import { useRef, useState } from 'react';
import { Modal } from '../../../components/Overlays';
import { Alert } from '../../../components/States';
import { useToast } from '../../../components/Toast';
import { useBrand, useBrandUpload } from '../../../lib/brand-api';
import { errorMessage } from '../../../lib/messages';

type Slot = 'logo' | Brand['kit'][number]['slot'];

/** "Trocar logo" / "Substituir {nome}" (Config-Negocio): a dropzone and the size hint. */
function ReplaceDialog({
  slot,
  title,
  hint,
  accept,
  onClose,
}: {
  slot: Slot;
  title: string;
  hint: string;
  accept: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const upload = useBrandUpload();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [tried, setTried] = useState(false);
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={upload.isPending}
            onClick={() => {
              setTried(true);
              if (!file) return;
              upload.mutate(
                { slot, file },
                {
                  onSuccess: () => {
                    toast(slot === 'logo' ? 'Logo trocado' : 'Arquivo do kit substituído');
                    onClose();
                  },
                },
              );
            }}
          >
            {upload.isPending ? 'Enviando…' : 'Substituir'}
          </button>
        </>
      }
    >
      <p className="sm muted">{hint}</p>
      <button
        type="button"
        className="card sunk col"
        style={{
          alignItems: 'center',
          gap: 8,
          padding: 24,
          border: '2px dashed var(--line)',
          cursor: 'pointer',
          font: 'inherit',
          color: 'inherit',
        }}
        onClick={() => input.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files[0];
          if (f) setFile(f);
        }}
      >
        <b className="sm">{file ? file.name : 'Arraste o arquivo aqui'}</b>
        <span className="btn sm">Escolher arquivo</span>
      </button>
      <input
        ref={input}
        type="file"
        hidden
        accept={accept}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) setFile(f);
        }}
      />
      {tried && !file ? (
        <p className="help err">Escolha o arquivo novo antes de substituir.</p>
      ) : null}
      {upload.isError ? <Alert tone="err">{errorMessage(upload.error)}</Alert> : null}
    </Modal>
  );
}

const LOGO_HINT =
  'SVG ou PNG com fundo transparente, até 2 MB. Sai no PDF do orçamento, na tela de entrada e no menu.';

/** "Logo" card: preview, "Trocar" and, for an uploaded one, "Remover" (Admin). */
export function LogoCard({ readOnly }: { readOnly: boolean }) {
  const brand = useBrand();
  const toast = useToast();
  const upload = useBrandUpload();
  const [open, setOpen] = useState(false);
  const logo = brand.data?.logo;
  return (
    <section className="card col" style={{ gap: 14 }}>
      <div className="row between">
        <h2>Logo</h2>
        <span className="xs muted">PNG ou SVG, fundo transparente</span>
      </div>
      <div
        className="card sunk"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 120,
          padding: 16,
        }}
      >
        {logo ? (
          <img src={logo.url} alt="Logo da loja" style={{ maxWidth: '100%', maxHeight: 80 }} />
        ) : null}
      </div>
      <p className="xs muted">
        {logo?.custom && logo.uploadedAt
          ? `${logo.name} · enviado em ${new Date(logo.uploadedAt).toLocaleDateString('pt-BR')}`
          : 'Logo do kit de marca (logo-horizontal-cor.svg).'}
      </p>
      {readOnly ? null : (
        <div className="row" style={{ gap: 8 }}>
          <button type="button" className="btn sm" onClick={() => setOpen(true)}>
            Trocar
          </button>
          {logo?.custom ? (
            <button
              type="button"
              className="btn sm g"
              disabled={upload.isPending}
              onClick={() =>
                upload.mutate(
                  { slot: 'logo', file: null },
                  {
                    onSuccess: () => toast('Logo removido'),
                    onError: (err) => toast(errorMessage(err)),
                  },
                )
              }
            >
              Remover
            </button>
          ) : null}
        </div>
      )}
      {open ? (
        <ReplaceDialog
          slot="logo"
          title="Trocar logo"
          hint={LOGO_HINT}
          accept="image/png,image/svg+xml,.svg"
          onClose={() => setOpen(false)}
        />
      ) : null}
    </section>
  );
}

const KIT_BG: Record<string, string> = {
  wm1: '#1D1D1D',
  wm2: '#1D1D1D',
  wm3: '#FBEFE2',
  ovf: '#FFD9D3',
  ovr: '#FFD9D3',
  ovh: '#FFD9D3',
  enc: '#0F5D6B',
};

/** "Kit de marca": the seven files used to export photos and videos, each with "Substituir". */
export function BrandKitCard({ readOnly }: { readOnly: boolean }) {
  const brand = useBrand();
  const [open, setOpen] = useState<Brand['kit'][number] | null>(null);
  return (
    <section className="card col" style={{ gap: 10 }}>
      <div className="row between wrap" style={{ gap: 8 }}>
        <h2>Kit de marca</h2>
        <span className="xs muted">usado ao exportar fotos e vídeos em Mídia</span>
      </div>
      {(brand.data?.kit ?? []).map((k) => (
        <div key={k.slot} className="row" style={{ gap: 12 }}>
          <span
            style={{
              width: 56,
              height: 40,
              flex: '0 0 56px',
              borderRadius: 8,
              background: KIT_BG[k.slot],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <img src={k.url} alt="" style={{ maxWidth: '90%', maxHeight: '90%' }} />
          </span>
          <span className="grow" style={{ minWidth: 0 }}>
            <span className="sm" style={{ display: 'block', fontWeight: 500 }}>
              {k.name}
            </span>
            <span className="xs muted trunc" style={{ display: 'block' }}>
              {k.file} · {k.slot.startsWith('wm') ? `${k.width} px` : `${k.width}×${k.height}`} ·{' '}
              {k.use}
              {k.replacedAt
                ? ` · trocado em ${new Date(k.replacedAt).toLocaleDateString('pt-BR')}`
                : ''}
            </span>
          </span>
          {readOnly ? null : (
            <button type="button" className="btn sm" onClick={() => setOpen(k)}>
              Substituir
            </button>
          )}
        </div>
      ))}
      {open ? (
        <ReplaceDialog
          slot={open.slot}
          title={`Substituir ${open.name}`}
          hint={`Use o mesmo tamanho (${open.slot.startsWith('wm') ? `${open.width} px` : `${open.width}×${open.height}`}). Os vídeos e fotos já exportados não mudam.`}
          accept="image/png"
          onClose={() => setOpen(null)}
        />
      ) : null}
    </section>
  );
}
